import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Play, FileSpreadsheet, FileDown, Loader2 } from "lucide-react";

export default function ReportPage() {
  const { reportKey } = useParams();
  const [report, setReport] = useState(null);
  const [schemas, setSchemas] = useState([]);
  const [selectedSchema, setSelectedSchema] = useState("");
  const [filterValues, setFilterValues] = useState({});
  const [results, setResults] = useState(null);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    setResults(null);
    setColumns([]);
    setFilterValues({});
    const load = async () => {
      try {
        const [reportsRes, schemasRes] = await Promise.all([
          api.get("/reports"),
          api.get("/schemas"),
        ]);
        const found = reportsRes.data.find((r) => r.key === reportKey);
        setReport(found || null);
        setSchemas(schemasRes.data);
        if (schemasRes.data.length > 0) setSelectedSchema(schemasRes.data[0]);
      } catch (e) {
        toast.error("Failed to load report");
      } finally {
        setPageLoading(false);
      }
    };
    load();
  }, [reportKey]);

  const runReport = async () => {
    if (!selectedSchema) {
      toast.error("Please select a schema");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/reports/run", {
        report_key: reportKey,
        schema: selectedSchema,
        filters: filterValues,
      });
      setColumns(data.columns);
      setResults(data.data);
      toast.success(`${data.row_count} rows returned`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Query execution failed");
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format) => {
    if (!selectedSchema) return;
    setExporting(true);
    try {
      const response = await api.post(
        "/reports/export",
        {
          report_key: reportKey,
          schema: selectedSchema,
          filters: filterValues,
          format,
        },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${report?.name || "report"}.${format === "excel" ? "xlsx" : "csv"}`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const updateFilter = (key, value) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  if (pageLoading) {
    return (
      <div className="flex items-center gap-3 text-[#A3A3A3] py-12">
        <Loader2 size={20} className="animate-spin" /> Loading...
      </div>
    );
  }

  if (!report) {
    return (
      <div className="text-[#A3A3A3] py-12" data-testid="report-not-found">
        Report not found
      </div>
    );
  }

  return (
    <div data-testid="report-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1
            className="text-2xl font-bold tracking-tight text-white"
            data-testid="report-title"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            {report.name}
          </h1>
          {report.description && (
            <p className="text-sm text-[#A3A3A3] mt-1">{report.description}</p>
          )}
        </div>
        {results && (
          <div className="flex items-center gap-2">
            {report.export_excel && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportReport("excel")}
                disabled={exporting}
                data-testid="export-excel-button"
                className="border-[#262626] text-white hover:bg-[#1E1E1E]"
              >
                <FileSpreadsheet size={16} className="mr-2" /> Excel
              </Button>
            )}
            {report.export_csv && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportReport("csv")}
                disabled={exporting}
                data-testid="export-csv-button"
                className="border-[#262626] text-white hover:bg-[#1E1E1E]"
              >
                <FileDown size={16} className="mr-2" /> CSV
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Filter Bar */}
      <div
        className="bg-[#141414] border border-[#262626] rounded-md p-4 mb-6"
        data-testid="filter-bar"
      >
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-[#A3A3A3] mb-1.5 block">
              Schema
            </label>
            <Select value={selectedSchema} onValueChange={setSelectedSchema}>
              <SelectTrigger
                className="w-[200px] bg-[#0A0A0A] border-[#262626]"
                data-testid="schema-select"
              >
                <SelectValue placeholder="Select schema" />
              </SelectTrigger>
              <SelectContent>
                {schemas.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {report.filters?.map((filter) => (
            <div key={filter.key}>
              <label className="text-xs font-semibold tracking-wider uppercase text-[#A3A3A3] mb-1.5 block">
                {filter.label}
                {filter.required && <span className="text-red-400 ml-1">*</span>}
              </label>
              {filter.type === "date_range" ? (
                <div className="flex gap-2">
                  <Input
                    type="date"
                    className="bg-[#0A0A0A] border-[#262626] w-[160px]"
                    data-testid={`filter-${filter.key}-from`}
                    onChange={(e) =>
                      updateFilter(`${filter.key}_from`, e.target.value)
                    }
                  />
                  <Input
                    type="date"
                    className="bg-[#0A0A0A] border-[#262626] w-[160px]"
                    data-testid={`filter-${filter.key}-to`}
                    onChange={(e) =>
                      updateFilter(`${filter.key}_to`, e.target.value)
                    }
                  />
                </div>
              ) : filter.type === "select" ? (
                <Select
                  onValueChange={(val) => updateFilter(filter.key, val)}
                >
                  <SelectTrigger className="w-[200px] bg-[#0A0A0A] border-[#262626]">
                    <SelectValue placeholder={`Select ${filter.label}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {(filter.options || []).map((opt) => (
                      <SelectItem key={opt} value={opt}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  type={filter.type === "number" ? "number" : "text"}
                  className="bg-[#0A0A0A] border-[#262626] w-[200px]"
                  placeholder={filter.label}
                  data-testid={`filter-${filter.key}`}
                  onChange={(e) => updateFilter(filter.key, e.target.value)}
                />
              )}
            </div>
          ))}

          <Button
            onClick={runReport}
            disabled={loading}
            data-testid="run-report-button"
            className="bg-[#0066FF] hover:bg-[#005CE6] text-white"
          >
            {loading ? (
              <Loader2 size={16} className="mr-2 animate-spin" />
            ) : (
              <Play size={16} className="mr-2" />
            )}
            Run
          </Button>
        </div>
      </div>

      {/* Results */}
      {results && (
        <div
          className="bg-[#141414] border border-[#262626] rounded-md"
          data-testid="results-container"
        >
          <div className="p-3 border-b border-[#262626] flex items-center justify-between">
            <span className="text-sm text-[#A3A3A3]" data-testid="row-count">
              {results.length} rows
            </span>
          </div>
          <div className="table-scroll-wrapper">
            <Table>
              <TableHeader>
                <TableRow className="border-[#262626] hover:bg-transparent">
                  {columns.map((col) => (
                    <TableHead
                      key={col}
                      className="text-xs font-semibold tracking-wider uppercase text-[#A3A3A3] whitespace-nowrap sticky top-0 bg-[#141414]"
                    >
                      {col}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="text-center text-[#A3A3A3] py-8"
                    >
                      No results
                    </TableCell>
                  </TableRow>
                ) : (
                  results.map((row, i) => (
                    <TableRow key={i} className="border-[#262626] hover:bg-[#1E1E1E]">
                      {columns.map((col) => (
                        <TableCell
                          key={col}
                          className="text-sm whitespace-nowrap text-white"
                        >
                          {row[col] != null ? String(row[col]) : ""}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
